import { useEffect, useState } from 'react';

import useUserStore from 'store/user';

import ChevronRight from 'assets/images/chevron_right.svg?react';
import { useNavigate } from 'react-router-dom';
import { readAssetInfo } from '@/api/balancer/read';
import {
  type AddIndexResReadPointAssetHistoryType,
  type ResReadPointAssetHistoryType,
  type ResReadPointAssetInfoType,
  type ResReadPointAssetsType,
} from '@/types';
import { DEFAULT_DAY, SUCCESS } from '@/lib/name-space';
import { getDayWithSuffix } from '@/utils/format';
import { now } from '@/lib/dayjs';
import RewardHistory from '@/components/asset-status/RewardHistory';
import InfoTab from '@/components/asset-status/InfoTab';

export default function MyAssetStatus() {
  // store
  const { ids } = useUserStore();

  // state
  const [info, setInfo] = useState<ResReadPointAssetInfoType[] | []>([]);
  const [history, setHistory] = useState<AddIndexResReadPointAssetHistoryType[] | []>([]);
  const [earned, setEarned] = useState({
    properties: 0,
    income: 0,
    maxTap: -1,
    currentTab: 0,
  });

  const [tab, setTab] = useState(earned.currentTab);
  const navigate = useNavigate();

  useEffect(() => {
    if (info.length > 0) {
      setEarned((prev) => ({
        ...prev,
        currentTab: tab - 1,
      }));
    }
    return () => {};
  }, [tab, info]);

  useEffect(() => {
    (async () => {
      if (!ids.pointId) {
        navigate('/');
        return;
      }
      const re = await readAssetInfo(ids);
      if (re.status !== SUCCESS) {
        return;
      }
      const data: ResReadPointAssetsType = re.data;
      const infos = data.info.map((item: ResReadPointAssetInfoType) => ({
        ...item,
        start_dt: now(item.start_dt, DEFAULT_DAY),
        end_dt: now(item.end_dt, DEFAULT_DAY),
        reward_dt: getDayWithSuffix(Number(item.reward_dt)),
      }));
      setInfo(infos ?? []);

      // usdt만 따로 빼서 합산
      const usdtSum = data.info.reduce((acc, item) => acc + Number(item.usdt ?? 0), 0);
      const inter = calculateInterest(data.info);
      setEarned({
        properties: usdtSum ?? 0,
        income: truncateTo6Decimals(inter) ?? 0,
        maxTap: data.info.length - 1,
        currentTab: 0,
      });
      if (data.info.length > 0) {
        setTab(1);
      } else {
        setTab(0);
      }
      const historys = data.history.map((item: ResReadPointAssetHistoryType, index: number) => ({
        ...item,
        reward_dt: now(item.reward_dt, DEFAULT_DAY),
        index: index + 1,
      }));
      setHistory(historys ?? []);
    })();

    return () => {};
  }, []);

  const calculateInterest = (info: ResReadPointAssetInfoType[]): number => {
    return info.reduce((total, pool) => {
      const usdt = parseFloat(pool.usdt);
      const interestRate = parseFloat(pool.interest_rate);
      const dailyRate = interestRate / 100 / 365;
      const reward = usdt * dailyRate * 30;
      return total + reward;
    }, 0);
  };
  const truncateTo6Decimals = (num: number): number => {
    return Math.floor(num * 1_000_000) / 1_000_000;
  };
  return (
    <>
      <div className="px-[16px] pt-[20px]">
        <div className="mb-[10px] flex items-center gap-[5px]">
          <div className="mb-[3px] h-[8px] w-[8px] rounded-full bg-[#FF4F19]" />
          <h3 className="text-[15px] font-[800] text-[#FF4F19]">MY ASSET STATUS</h3>
        </div>

        <div className="flex flex-col gap-[7px]">
          <div className="flex justify-between rounded-lg border-t-2 border-t-[#B2B2B2] bg-white px-[10px] py-[14px] text-[13px] text-[#4D4840]">
            <span className="text-[12px] font-bold text-[#4F4F4F]">My rentable Properties</span>
            <span className="text-[12px] text-[#FF4F19]">{earned.properties}$</span>
          </div>

          <div className="flex justify-between rounded-lg border-t-2 border-t-[#B2B2B2] bg-white px-[10px] py-[14px] text-[13px] text-[#4D4840]">
            <span className="text-[12px] font-bold text-[#4F4F4F]">My Estimated Rental Income(M)</span>
            <span className="text-[12px] text-[#FF4F19]">{earned.income}$</span>
          </div>
        </div>
        <p className="p-[10px] text-[8px] leading-[12px] text-[#FF4F19]">
          * Earn 3,000P to get $1 in asset value – collect up to 9,000P for a maximum of $3! At 9,000P or more, your
          monthly rental income will be applied when the property is rented.
        </p>
      </div>
      <div className="bg-transparent">
        {info && <InfoTab info={info} setTab={setTab} tab={tab} />}
        {tab >= 1 && (
          <div className="bg-[#FF6535] py-[17px]">
            {/* Staking Pool Info */}
            <div className="px-[17px]">
              <div className="flex items-center gap-[5px]">
                <div className="mb-[3px] h-[8px] w-[8px] rounded-full bg-white" />
                <h3 className="text-[15px] font-[800] text-white">Staking Pool Info</h3>
              </div>

              <ul className="mt-[11px] rounded-lg border-t border-[#B2B2B2] bg-white px-[10px] py-[11px] text-[10px] text-[#4F4F4F]">
                {info && info[earned.maxTap] && (
                  <>
                    <div className="flex items-center gap-[2px]">
                      <ChevronRight className="mb-[3px] h-[8px] w-[6px]" />
                      <span className="font-bold">Pool Name</span>
                      <span className="ml-auto">{info[earned.currentTab].title}</span>
                    </div>
                    <div className="mb-[12px] mt-[8px] h-[1px] rounded-sm bg-[#B2B2B2]" />

                    <div className="flex items-center gap-[2px]">
                      <ChevronRight className="mb-[3px] h-[8px] w-[6px]" />
                      <span className="font-bold">Staking Period</span>
                      <span className="ml-auto">
                        {info[earned.currentTab].start_dt} ~ {info[earned.currentTab].end_dt}
                      </span>
                    </div>
                    <div className="mb-[12px] mt-[8px] h-[1px] rounded-sm bg-[#B2B2B2]" />

                    <div className="flex items-center gap-[2px]">
                      <ChevronRight className="mb-[3px] h-[8px] w-[6px]" />
                      <span className="font-bold">Reward Data</span>
                      <span className="ml-auto">Monthly on the {info[earned.currentTab].reward_dt}</span>
                    </div>
                    <div className="mb-[12px] mt-[8px] h-[1px] rounded-sm bg-[#B2B2B2]" />
                    <div className="flex items-center gap-[2px]">
                      <ChevronRight className="mb-[3px] h-[8px] w-[6px]" />
                      <span className="font-bold">APR</span>
                      <span className="ml-auto">{info[earned.currentTab].interest_rate}%</span>
                    </div>
                  </>
                )}
              </ul>
            </div>

            {/* Reward History */}
            {history && <RewardHistory history={history} />}
          </div>
        )}
      </div>
    </>
  );
}
